import axios from "axios";
import { message } from "antd";
import { Encrypt } from "./encryption";
import { userManagementApi } from "../config/api";
import { Session } from "../storage/session";
import { getCashfree } from "../components/CashfreeSdk";
import { SUBSCRIPTION_CONFIG } from "../config/zibuzzConfig";

/**
 * Extract Cashfree payment context from API response
 * Handles nested response structures from backend
 */
export function extractCashfreePaymentContext(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  const candidates = [data, data.data, data.data?.data].filter(
    (x) => x && typeof x === "object" && !Array.isArray(x)
  );

  let payment_session_id = null;
  let order_id = null;

  for (const r of candidates) {
    if (!payment_session_id) {
      payment_session_id = r.payment_session_id ?? r.paymentSessionId ?? null;
    }
    if (!order_id) {
      order_id = r.order_id ?? r.orderId ?? null;
    }

    if (payment_session_id && order_id) {
      break;
    }
  }

  if (!payment_session_id) {
    return null;
  }

  return { payment_session_id, order_id };
}

/**
 * Update session storage and auth context with new token
 * Common pattern used after trial activation and payment validation
 */
export function updateSessionToken(newToken, setToken) {
  if (!newToken) return false;

  const prev = Session.get();
  Session.save({
    ...prev,
    token: newToken,
    status: prev?.status || "success",
  });
  setToken(newToken);
  return true;
}

/**
 * Activate trial period for new users
 * @param {string} token - User JWT token
 * @param {number} trialDays - Number of trial days (defaults to config)
 * @returns {Promise<{success: boolean, token?: string, message?: string}>}
 */
export async function activateTrial(token, trialDays = SUBSCRIPTION_CONFIG.TRIAL_DAYS) {
  if (!token) {
    throw new Error("Token is required");
  }

  try {
    const response = await axios.post(
      userManagementApi,
      {
        ...Encrypt({
          token,
          action: "activateTrial",
          trialDays,
        }),
        operationType: "manageSubscription",
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const { token: newToken, success, message: msg } = response.data || {};

    if (success === false) {
      return {
        success: false,
        message: msg || "Could not activate trial.",
      };
    }

    return {
      success: true,
      token: newToken,
      message: msg || `${trialDays}-day trial activated successfully!`,
    };
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to activate trial."
    );
  }
}

/**
 * Create payment order for subscription renewal/upgrade
 * @param {string} token - User JWT token
 * @param {string} targetPlan - Target plan tier (solo/galaxy)
 * @param {string} validity - Billing cycle (monthly/quarterly/annual)
 * @param {object} userProfile - User profile object
 * @returns {Promise<{payment_session_id: string, order_id: string, adjustmentInfo?: object}>}
 */
export async function createPaymentOrder(token, targetPlan, validity, userProfile = null) {
  if (!token || !targetPlan || !validity) {
    throw new Error("Token, targetPlan, and validity are required");
  }

  try {
    const encryptedPayload = Encrypt({
      token,
      action: "renewPlan",
      subscription: {
        targetPlan,
        validity,
      },
      ...(userProfile && { userProfile }),
    });

    const response = await axios.post(
      userManagementApi,
      {
        ...encryptedPayload,
        operationType: "manageSubscription",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const paymentCtx = extractCashfreePaymentContext(response.data);

    if (!paymentCtx?.payment_session_id) {
      // Check if it's a direct success (e.g., free upgrade with credits)
      const { token: newToken, success, message: msg } = response.data || {};
      if (success !== false && newToken) {
        return {
          directSuccess: true,
          token: newToken,
          message: msg || "Subscription updated successfully!",
        };
      }
      throw new Error(msg || "Failed to create payment session.");
    }

    // Validate order_id exists
    if (!paymentCtx.order_id) {
      throw new Error("Order ID not returned from payment gateway");
    }

    // Extract adjustment info if plan switching
    const adjustInfo =
      response.data?.adjustmentInfo || response.data?.data?.adjustmentInfo || null;

    return {
      payment_session_id: paymentCtx.payment_session_id,
      order_id: paymentCtx.order_id,
      adjustmentInfo: adjustInfo,
    };
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Could not create payment order."
    );
  }
}

/**
 * Open Cashfree payment modal and handle checkout flow
 * @param {string} payment_session_id - Cashfree payment session ID
 * @returns {Promise<{error?: object}>}
 * @note Cashfree returns undefined on success, {error: ...} on failure
 */
export async function openCashfreeCheckout(payment_session_id) {
  if (!payment_session_id) {
    throw new Error("Payment session ID is required");
  }

  try {
    const cashfree = await getCashfree();
    const checkoutResult = await cashfree.checkout({
      paymentSessionId: payment_session_id,
      redirectTarget: "_modal",
    });

    // Cashfree returns {error: ...} on failure, undefined/empty on success
    if (checkoutResult?.error) {
      return {
        error: checkoutResult.error,
      };
    }
    return {}; // No error = success
  } catch (error) {
    throw new Error(
      error?.message || "Failed to open payment checkout."
    );
  }
}

/**
 * Validate payment after Cashfree checkout completion
 * @param {string} token - User JWT token
 * @param {string} payment_session_id - Cashfree payment session ID
 * @param {string} order_id - Order ID
 * @param {object} userProfile - User profile object
 * @returns {Promise<{success: boolean, token?: string, message?: string}>}
 */
export async function validatePayment(
  token,
  payment_session_id,
  order_id,
  userProfile = null
) {
  if (!token || !payment_session_id) {
    throw new Error("Token and payment session ID are required");
  }

  if (!order_id) {
    throw new Error("Order ID is required for payment validation");
  }

  try {
    const response = await axios.post(
      userManagementApi,
      {
        ...Encrypt({
          token,
          action: "validatePayment",
          payment_session_id,
          order_id,
          ...(userProfile && { userProfile }),
        }),
        operationType: "manageSubscription",
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const { success, token: newToken, message: msg } = response.data || {};

    if (success === false) {
      return {
        success: false,
        message: msg || "Payment could not be verified.",
      };
    }
    return {
      success: true,
      token: newToken,
      message: msg || "Subscription updated successfully!",
    };
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Could not confirm payment."
    );
  }
}

/**
 * Complete subscription flow: create order, open checkout, validate payment
 * This is a high-level helper that handles the full payment flow
 * @param {object} params - Flow parameters
 * @param {string} params.token - User JWT token
 * @param {string} params.targetPlan - Target plan tier
 * @param {string} params.validity - Billing cycle
 * @param {object} params.userProfile - User profile
 * @param {function} params.setToken - Auth context setToken function
 * @param {function} params.onSuccess - Success callback
 * @param {function} params.onError - Error callback
 * @param {function} params.onAdjustmentInfo - Adjustment info callback (for plan switching)
 * @returns {Promise<void>}
 */
export async function handleSubscriptionFlow({
  token,
  targetPlan,
  validity,
  userProfile,
  setToken,
  onSuccess,
  onError,
  onAdjustmentInfo,
}) {
  try {
    // Step 1: Create payment order
    const orderResult = await createPaymentOrder(token, targetPlan, validity, userProfile);

    // Handle direct success (e.g., free upgrade)
    if (orderResult.directSuccess) {
      if (orderResult.token) {
        updateSessionToken(orderResult.token, setToken);
      }
      message.success(orderResult.message);
      onSuccess?.();
      return;
    }

    // Validate order_id exists
    if (!orderResult.order_id) {
      const errorMsg = "Order ID missing from payment order response";
      message.error(errorMsg);
      onError?.(new Error(errorMsg));
      return;
    }

    // Notify about adjustment info if plan switching
    if (orderResult.adjustmentInfo && onAdjustmentInfo) {
      onAdjustmentInfo(orderResult.adjustmentInfo);
    }

    // Step 2: Open Cashfree checkout
    const checkoutResult = await openCashfreeCheckout(orderResult.payment_session_id);

    // Cashfree returns {error: ...} on failure, empty object on success
    if (checkoutResult.error) {
      message.error(checkoutResult.error?.message || "Payment checkout failed.");
      onError?.(checkoutResult.error);
      return;
    }

    // Step 3: Validate payment
    const validationResult = await validatePayment(
      token,
      orderResult.payment_session_id,
      orderResult.order_id,
      userProfile
    );

    if (!validationResult.success) {
      message.error(validationResult.message);
      onError?.(new Error(validationResult.message));
      return;
    }

    // Step 4: Update token and notify success
    if (validationResult.token) {
      updateSessionToken(validationResult.token, setToken);
    }
    message.success(validationResult.message);
    onSuccess?.();
  } catch (error) {
    message.error(error.message || "Subscription flow failed.");
    onError?.(error);
  }
}

/**
 * Handle trial activation flow
 * @param {object} params - Flow parameters
 * @param {string} params.token - User JWT token
 * @param {function} params.setToken - Auth context setToken function
 * @param {number} params.trialDays - Number of trial days
 * @param {function} params.onSuccess - Success callback
 * @param {function} params.onError - Error callback
 * @returns {Promise<void>}
 */
export async function handleTrialActivation({
  token,
  setToken,
  trialDays = SUBSCRIPTION_CONFIG.TRIAL_DAYS,
  onSuccess,
  onError,
}) {
  try {
    const result = await activateTrial(token, trialDays);

    if (!result.success) {
      message.error(result.message);
      onError?.(new Error(result.message));
      return;
    }

    if (result.token) {
      updateSessionToken(result.token, setToken);
    }
    message.success(result.message);
    onSuccess?.();
  } catch (error) {
    message.error(error.message || "Trial activation failed.");
    onError?.(error);
  }
}
